import React, { useMemo } from 'react';
import { Stack, Typography } from '@mui/material';

import { PAYMENT_STEP } from '~common';
import {
  BestValueBadge,
  PricingOption,
} from '~components/molecules/new-step-three/PricingOption';
import { LocalizationTypography } from '~components/organisms';
import { usePaymentStepContext, useStationPricingContext } from '~providers';
import { StationPricingModel } from '~types/pricing.type';

export const SelectRentalOptionStep: React.FC = () => {
  const {
    stationPricing,
    stationPricingDisplay,
    secondStationPricing,
    secondStationPricingDisplay,
    subscriptionPricings,
  } = useStationPricingContext();

  const { selectedPricing, handleSetSelectedPricing, handleSetPaymentStep } =
    usePaymentStepContext();

  /**
   * The pay-per-use options first, then the pass catalog — the backend already
   * excludes anything it returned as first/second pricing, so no de-duplication
   * is needed here.
   */
  const options = useMemo(() => {
    const payPerUse: StationPricingModel[] = [];

    if (stationPricing && stationPricingDisplay) {
      payPerUse.push({
        model: stationPricing,
        displayModel: stationPricingDisplay,
      });
    }

    if (secondStationPricing && secondStationPricingDisplay) {
      payPerUse.push({
        model: secondStationPricing,
        displayModel: secondStationPricingDisplay,
      });
    }

    return [...payPerUse, ...subscriptionPricings];
  }, [
    stationPricing,
    stationPricingDisplay,
    secondStationPricing,
    secondStationPricingDisplay,
    subscriptionPricings,
  ]);

  /**
   * Only the first recurring pass is tagged. There is deliberately no
   * precedence between multiple matching passes — a coherent, non-overlapping
   * catalog is a product responsibility — so tagging more than one would be
   * making up an ordering the backend does not define.
   */
  const bestValuePricingId = useMemo(
    () => subscriptionPricings.find(({ model }) => model.autoRenew)?.model.id,
    [subscriptionPricings],
  );

  if (options.length < 2) {
    return null;
  }

  return (
    <Stack
      sx={{
        paddingBottom: 1.5,
        alignItems: 'center',
        gap: 3,
      }}
    >
      <LocalizationTypography
        textId="app.rentalPage.newStepThree.selectRentalOption.title"
        defaultText="Select rental option"
        variant="Poppins/Heading-3"
      />

      {options.map(({ model, displayModel }) => (
        <PricingOption
          key={model.id}
          displayModel={displayModel}
          isSelected={selectedPricing?.id === model.id}
          onSelect={() => {
            handleSetSelectedPricing(model);
            handleSetPaymentStep(PAYMENT_STEP.RENT_A_CHARGER);
          }}
          badge={
            model.id === bestValuePricingId ? <BestValueBadge /> : undefined
          }
          notice={
            model.displayMessage ? (
              <Typography variant="Poppins/Body-sm">
                {model.displayMessage}
              </Typography>
            ) : undefined
          }
        />
      ))}
    </Stack>
  );
};
